import { useQuery, UseQueryOptions, QueryKey } from 'react-query';
import { useDispatch } from 'react-redux';
import { ActionCreatorWithPayload, ActionCreatorWithoutPayload } from '@reduxjs/toolkit';
import { errorHandler } from '../utils/errorHandler';

interface ApiQueryActions<TData> {
  setLoading: ActionCreatorWithoutPayload;
  setSuccess: ActionCreatorWithPayload<TData>;
  setError: ActionCreatorWithPayload<string>;
}

interface UseApiQueryOptions<TData, TError> extends Omit<UseQueryOptions<TData, TError>, 'onSuccess' | 'onError'> {
  actions?: ApiQueryActions<TData>;
  showErrorToast?: boolean;
  customErrorMessage?: string;
}

export const useApiQuery = <TData = unknown, TError = Error>(
  queryKey: QueryKey,
  queryFn: () => Promise<TData>,
  options: UseApiQueryOptions<TData, TError>
) => {
  const dispatch = useDispatch();
  const { actions, showErrorToast = true, customErrorMessage, ...queryOptions } = options;

  const queryResult = useQuery<TData, TError>(
    queryKey,
    queryFn,
    {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      ...queryOptions,
      onSuccess: (data) => {
        if (actions) {
          dispatch(actions.setSuccess(data));
        }
      },
      onError: (error: any) => {
        const errorMessage = errorHandler.handleError(error, { 
          customMessage: customErrorMessage,
          showToast: showErrorToast 
        });
        if (actions) {
          dispatch(actions.setError(errorMessage));
        }
      }
    }
  );

  const refetchWithLoading = () => {
    if (actions) {
      dispatch(actions.setLoading());
    }
    return queryResult.refetch();
  };

  return {
    ...queryResult,
    refetch: refetchWithLoading
  };
};

export default useApiQuery;
